import { Field, Formik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import * as Yup from "yup";

import { IIdentity } from "@/customTypings/user/Identity";
import { Alert, Button, Col, Form, Modal, Row } from "react-bootstrap";
import { IRole } from "../../../../customTypings/user/Role";
import { rolesService } from "../../../../services";
import { userService } from "../../../../services";
import { Avatar } from "../../../common/Avatar/Avatar";
import { LoadingSpinner } from "../../../common/Loading/LoadingSpinner";
import {
    DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE,
    shouldShowFormFieldError,
    showToastError,
    showToastSuccess,
} from "../../../../utils";

type Props = {
    show: boolean;
    setShow: React.Dispatch<React.SetStateAction<any>>;
    user: IIdentity;
    setUserInteraction?: React.Dispatch<React.SetStateAction<any>>;
};

type RoleFormValues = { roles: string[] };

const userRolesValidationSchema = Yup.object({
    roles: Yup.array()
        .of(Yup.string().required())
        .min(1, "Select at least one role.")
        .required(),
});

const UserRoleEdit: React.FC<Props> = ({ show, setShow, user, setUserInteraction }) => {
    const [alertVariant, setAlertVariant] = useState("danger");
    const [allRoles, setAllRoles] = useState<IRole[]>();
    const [liveValidationAfterSubmit, setLiveValidationAfterSubmit] = useState(false);

    const validationSchema = useMemo(() => userRolesValidationSchema, []);

    const handleClose = () => setShow(false);

    useEffect(() => {
        rolesService.getAllForGroup(0).then((roles: IRole[]) => setAllRoles(roles));
    }, []);

    return (
        <Modal centered show={show} keyboard={false} onHide={handleClose}>
            {allRoles && (
                <Formik<RoleFormValues>
                    enableReinitialize
                    key={user.id}
                    initialValues={{
                        roles: user.roles.map((role) => role.id),
                    }}
                    validationSchema={validationSchema}
                    validateOnMount={false}
                    validateOnChange
                    validateOnBlur
                    onSubmit={async (values, { setStatus, setSubmitting }) => {
                        setLiveValidationAfterSubmit(true);
                        setStatus(undefined);
                        setSubmitting(true);
                        try {
                            const response: any = await userService.updateRoles(user.id, values.roles);
                            if (response.status !== "Failure") {
                                setLiveValidationAfterSubmit(false);
                                if (setUserInteraction !== undefined) {
                                    setUserInteraction(true);
                                }
                                showToastSuccess("User Role updated successfully!");
                                setTimeout(() => handleClose(), 1000);
                            } else {
                                setAlertVariant("danger");
                                showToastError(DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE);
                            }
                        } catch {
                            setAlertVariant("danger");
                            showToastError(DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE);
                        } finally {
                            setSubmitting(false);
                        }
                    }}
                >
                    {({ status, isSubmitting, handleSubmit, errors, submitCount }) => {
                        const hasPersistedRecord = true;
                        const showErr = (field: keyof RoleFormValues) =>
                            shouldShowFormFieldError(
                                errors,
                                field,
                                hasPersistedRecord,
                                submitCount,
                                liveValidationAfterSubmit
                            );
                        return (
                            <Form noValidate onSubmit={handleSubmit}>
                                <Modal.Header closeButton>
                                    <Modal.Title>Edit Roles</Modal.Title>
                                </Modal.Header>
                                <Modal.Body>
                                    <h5 className="mb-3">
                                        <Avatar identity={user} />
                                    </h5>

                                    <Form.Group as={Row} className="mb-3" controlId="roleCheckboxs">
                                        <Form.Label column sm={3}>
                                            Roles <span className="text-danger">*</span>
                                        </Form.Label>
                                        <Col sm={9} className="pt-2">
                                            {allRoles.map((role) => (
                                                <label key={role.id} className="d-block">
                                                    <Field
                                                        type="checkbox"
                                                        name="roles"
                                                        value={role.id}
                                                        className="form-check-input me-2"
                                                    />
                                                    {role.name}
                                                </label>
                                            ))}
                                            {showErr("roles") && (
                                                <div className="invalid-feedback d-block">{errors.roles as string}</div>
                                            )}
                                        </Col>
                                    </Form.Group>
                                    {status && (
                                        <Alert variant={alertVariant} className="mt-3">
                                            {status}
                                        </Alert>
                                    )}
                                </Modal.Body>
                                <Modal.Footer>
                                    <div className="form-group">
                                        <Button
                                            variant="primary"
                                            disabled={isSubmitting}
                                            type="submit"
                                            className="me-2"
                                        >
                                            {isSubmitting ? <LoadingSpinner text="Updating..." /> : "Update"}
                                        </Button>
                                        <Button variant="secondary" onClick={handleClose}>
                                            Cancel
                                        </Button>
                                    </div>
                                </Modal.Footer>
                            </Form>
                        );
                    }}
                </Formik>
            )}
        </Modal>
    );
};

export { UserRoleEdit };
